import {REPORT_TYPE} from '../../../constants';

/**
 * How a report came to exist, as stored on it. Absent on reports that predate the field.
 *
 * Widened to a plain string rather than the generated `REPORT_SOURCE` union on purpose. Only 'AI'
 * carries meaning here and everything else means standard, so relay's `%future added value` member
 * -- and any origin added after it -- lands on the conservative answer without this module or its
 * tests having to depend on a generated artifact that may not have been compiled yet.
 */
export type ReportOrigin = string | null | undefined;

/**
 * The translation key naming a report's type in the Reports list, or null to leave the stored type
 * name exactly as it is.
 *
 * Only a type a reader can meet twice - once built through the report pages, once built in the
 * report builder - carries a "(Standard)"/"(Custom)" suffix, because the suffix exists to separate
 * two reports sharing a type name. Project status reaches the same answer from the other direction,
 * having only ever existed in the builder, so it carries no suffix either.
 *
 * Utilization and project portfolio reports fall through to their stored name. That is a decision
 * about what the builder emits today, not a guarantee about what it can: no producer names either
 * type, since the drafting agent's whole vocabulary is 'Task report' and 'Project Status' and it
 * coerces anything else to the former. But `buildSaveReportAsInput` preserves an unresolved
 * `reportType` verbatim rather than relabelling it, so the value is schema-reachable, and a
 * builder-saved report of one of those types would sit here un-suffixed with only the AI marker
 * telling it apart. If a producer ever emits one, give it a case rather than trusting this note.
 *
 * `source` is the only signal that separates the two origins. `reportService` looks like it should
 * and does not: it is a transport flag naming which backend owns the report, and it is set true for
 * standard task and time registration reports too (NoReports.js), so keying off it would label a
 * standard report "(Custom)".
 *
 * `builderEnabled` gates the whole scheme, and does so only to sequence the release. The names are
 * meant to apply to everyone once the report builder is live; until then a company that
 * has only ever had standard reports would meet a "(Standard)" suffix with nothing to contrast it
 * against and no feature to explain it. So this gate is temporary: when the builder reaches general
 * availability, drop the parameter rather than leaving the scheme switched off for anyone.
 *
 * It is passed in rather than read here so this stays a pure function - reading the flag pulls in
 * FeatureUtil, and with it the util.js module graph, which would make every caller's test heavy.
 *
 * The stored type is deliberately not rewritten anywhere. It is also the URL slug that opens a saved
 * report (saved_report_util.js) and the `reportType` sent when sharing one, so it has to keep its
 * stored spelling; this returns a key for the label alone.
 */
export const reportTypeLabelId = (
	type: string | null | undefined,
	source: ReportOrigin,
	builderEnabled: boolean
): string | null => {
	if (!builderEnabled) {
		return null;
	}

	const custom = source === 'AI';

	switch (type) {
		case REPORT_TYPE.TASK_REPORT:
			return custom ? 'report.type.task_custom' : 'report.type.task_standard';
		case REPORT_TYPE.TIME_REG:
			return custom ? 'report.type.time_registration_custom' : 'report.type.time_registration_standard';
		case REPORT_TYPE.PROJECT_STATUS:
			return 'report.type.project_status';
		// One label, not a custom/standard pair: unlike a task or time report, this type has no
		// standard-page counterpart to be told apart from. Without a case here the Reports list left
		// the Type column blank for every audit report, which reads as a report with no type rather
		// than as one the list has not been taught about.
		case REPORT_TYPE.TIME_REG_AUDIT_LOG:
			return 'report.type.time_approval_audit_log';
		default:
			return null;
	}
};
